import { useMemo } from "react";
import {
  Flex,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorModeValue,
  useTheme,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { useLocation } from "react-router-dom";

import { useThemeContext } from "../hooks/theme-context";
import Logo from "../components/common/Logo";
import Navbar from "./Navbar";
import Settings from "./Settings";
import MobileNavbar from "./MobileNavbar";
import darkMode from "../assets/dark_mode.svg";
import lightMode from "../assets/light_mode.svg";

interface Props {
  isAuthenticated?: boolean;
  publicOnly?: boolean;
}

// Match the existing guest controls without mounting account, notification,
// translation or support components on the anonymous teaching routes.
const PublicHeaderControls = () => {
  const theme = useTheme();
  const { colorMode, toggleColorMode } = useThemeContext();
  const label = colorMode === "dark" ? "Light Mode" : "Dark Mode";
  const icon = colorMode === "dark" ? lightMode : darkMode;

  return (
    <>
      <Flex sx={theme.components.VisibleOnPc.baseStyle} align="center">
        <IconButton
          aria-label={label}
          icon={<Image src={icon} h="24px" alt="" />}
          onClick={toggleColorMode}
          variant="unstyled"
          display="flex"
          minW="24px"
          h="24px"
        />
      </Flex>
      <Flex sx={theme.components.VisibleOnMob.baseStyle} align="center">
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<HamburgerIcon />}
            variant="outline"
          />
          <MenuList>
            <MenuItem
              icon={<Image src={icon} boxSize="20px" alt="" />}
              onClick={toggleColorMode}
            >
              {label}
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </>
  );
};

const Header = ({ isAuthenticated = false, publicOnly = false }: Props) => {
  const theme = useTheme();
  const { headerBg } = useThemeContext();
  const location = useLocation();

  const isNavbarRequired = useMemo(
    () =>
      !(
        location.pathname.includes("signup") ||
        location.pathname.includes("verify-email")
      ) &&
      isAuthenticated &&
      !publicOnly,
    [location, isAuthenticated, publicOnly],
  );

  return (
    <Flex
      as="header"
      align="center"
      w="full"
      position="fixed"
      p={4}
      bg={headerBg}
      zIndex={10}
      boxShadow="md"
      height="70px"
      borderBottom="1px solid"
      borderColor={useColorModeValue("#D9D9D9", "#515155")}
      justify="space-between"
    >
      <Flex align="center">
        <Logo />
        {isNavbarRequired && (
          <Flex
            flex="1"
            justify="center"
            align="center"
            ml={24}
            sx={theme.components.VisibleOnPc.baseStyle}
          >
            <Navbar />
          </Flex>
        )}
      </Flex>
      {publicOnly ? (
        <PublicHeaderControls />
      ) : (
        <>
          <Flex sx={theme.components.VisibleOnPc.baseStyle} align="center">
            <Settings isAuthenticated={isAuthenticated} />
          </Flex>
          <MobileNavbar
            isAuthenticated={isAuthenticated}
            isNavbarRequired={isNavbarRequired}
          />
        </>
      )}
    </Flex>
  );
};

export default Header;
